﻿import { Flex, GapSize } from "@/components/atoms/flex";
import {
	ActionItemProps,
	ItemData,
	MultiSelectItemProps,
	Multiselect,
} from "../multiselect";
import { useStyle } from "./style";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import {
	Button,
	Divider,
	Menu,
	MenuItem,
	MenuList,
	MenuPopover,
	MenuTrigger,
	mergeClasses,
	SplitButton,
	Spinner,
} from "@fluentui/react-components";
import {
	EmailEditorProps,
	EmailData,
	RecipientData,
	UploadStatus,
} from "./types";
import {
	BinRecycle20Regular,
	Dismiss16Regular,
	MoreHorizontalRegular,
} from "@fluentui/react-icons";
import { FileInput } from "@/components/atoms/fileInput";
import { LexicalEditor } from "lexical";
import { EmailAction, Recipient } from "@/repository/models/message";
import { useInlineAttachments } from "../email/hooks";
import {
	sanitizeEmailBody,
	calculateAttachmentsSize,
	handleEmailPaste,
} from "@/common/helpers/emailHelper";
import { isValidEmail } from "@/common/helpers/validationHelper";
import {
	searchContactsByEmail,
	fetchMessageBody,
	MessageBodyKey,
} from "@/repository/queryFns";
import { ShortContactInfo } from "@/repository/models";
import { useTextMonitoring } from "@/hooks/supervisor";
import { TemplateDropdown } from "@/components/organisms/templates";
import type { MenuButtonProps } from "@fluentui/react-components";
import {
	SendType,
	SendTypeToLabelMapping,
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { AttachmentsView } from "./AttachmentsView";
import { EmailSubjectInput } from "./EmailSubjectInput";
import LexicalEditorWrapper from "../htmlEditor/LexicalEditorWrapper";
import { TemplateType } from "@/repository/models/templates";
import { InsertType } from "@/common/types/lexicalEditor";
import { useEmailAttachments } from "./useEmailAttachments";
import { useDragAndDrop } from "@/hooks/dragAndDrop";
import { useEmailAttachmentLimits } from "./useEmailAttachmentLimits";
import { TeamsThemeType } from "@/common/theme/teamsThemeType";
import { useTeamsTheme } from "@/hooks/theme";
import { useQuery } from "@tanstack/react-query";
import { AiIndicator } from "@/components/atoms/aiIndicator";
import { useIsAicLockedForInteraction } from "@/hooks/aicLocking";

enum RecipientType {
	To = "To",
	Cc = "Cc",
	Bcc = "Bcc",
}

type SearchState = Record<RecipientType, string>;

export const ConversationEmailEditor = (props: EmailEditorProps) => {
	const intl = useIntl();
	const classes = useStyle();
	const isDarkTheme = useTeamsTheme() === TeamsThemeType.Dark;

	const { totalBytes: emailMaxTotalAttachmentSizeBytes, perFileBytes: emailMaxAttachmentSizeBytes } = useEmailAttachmentLimits();

	const {
		data,
		onChange,
		onSendClick,
		closeEditor,
		sessionId,
		isSendDisabled,
		onAttachmentDeleted,
		draftParentMessageId,
		emailAction,
	} = props;
	const getEnumValues = (enumType: any) => {
		return Object.values(enumType).filter(
			(value) => typeof value === "number"
		) as (typeof enumType)[];
	};

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const isAicLocked = useIsAicLockedForInteraction(currentInteraction?.firstAgentAssignedAt);

	const currentInteractionIsPreview = useCurrentInteractionStore(
		(state: CurrentInteractionState) => Boolean(state.preview)
	);

	const sendTypes = useMemo(() => getEnumValues(SendType), []);

	const [visibleActions, setVisibleActions] = useState<Set<RecipientType>>(
		new Set([RecipientType.To])
	);

	const [hiddenRecipients, setHiddenRecipients] = useState<Set<RecipientType>>(
		new Set()
	);

	const [sendType, setSendType] = useState<SendType>(SendType.Send);
	const [hasAiContent, setHasAiContent] = useState(data.hasAiContent ?? false);
	const hasAiContentRef = useRef(data.hasAiContent ?? false);
	const [currentBody, setCurrentBody] = useState(data.body ?? "");
	const isBodyEmpty = (html: string) => !html?.replace(/<[^>]*>/g, "").trim();

	const [searchValues, setSearchValues] = useState<SearchState>({
		[RecipientType.To]: "",
		[RecipientType.Cc]: "",
		[RecipientType.Bcc]: "",
	});

	const handleSearchChange = (type: RecipientType, value: string) => {
		setSearchValues((prev) => ({
			...prev,
			[type]: value,
		}));
	};

	useEffect(() => {
		if (sendType !== currentInteraction?.sendType) {
			setSendType(currentInteraction?.sendType ?? SendType.Send);
		}
	}, [currentInteraction]);

	const isTextMonitoring = useTextMonitoring();

	const isReadOnlyMode = useMemo(() => {
		return isTextMonitoring || currentInteractionIsPreview;
	}, [isTextMonitoring, currentInteractionIsPreview]);

	useEffect(() => {
		setVisibleActions((prev) => {
			const next = new Set<RecipientType>(prev);
			next.add(RecipientType.To);

			if (
				(data.type == EmailAction.ReplyAll || !!data.cc.length) &&
				!hiddenRecipients.has(RecipientType.Cc)
			) {
				next.add(RecipientType.Cc);
			}

			if (!!data.bcc.length && !hiddenRecipients.has(RecipientType.Bcc)) {
				next.add(RecipientType.Bcc);
			}

			return next;
		});
	}, [data, hiddenRecipients]);

	const addReceiver = (type: RecipientType, value: Recipient) => {
		const propertyKey = type.toLowerCase() as keyof EmailData;
		const recipients = data[propertyKey] as Recipient[];
		const email = value.address.trim();

		if (recipients.some((item: Recipient) => item.address === email)) {
			return;
		}

		onChange(
			{
				...data,
				[propertyKey]: [
					...recipients,
					{ name: value.name, address: email, isValid: isValidEmail(email) },
				],
			},
			false,
			true,
			"addReceiver"
		);
	};

	const removeRecipient = (type: RecipientType, value: string) => {
		const propertyKey = type.toLowerCase() as keyof EmailData;
		const recipients = data[propertyKey] as Recipient[];

		const updatedRecipients = recipients.filter((x: Recipient) =>
			x.address ? x.address !== value : x.name !== value
		);

		if (updatedRecipients.length === recipients.length) {
			return;
		}

		onChange(
			{
				...data,
				[propertyKey]: updatedRecipients,
			},
			false,
			true,
			"removeRecipient"
		);
	};

	const hideRecipient = (type: RecipientType) => {
		setHiddenRecipients((prev) => new Set(prev).add(type));

		setVisibleActions((prev) => {
			const next = new Set(prev);
			next.delete(type);

			return next;
		});

		const propertyKey = type.toLowerCase() as keyof EmailData;
		if ((data[propertyKey] as Recipient[]).length) {
			onChange(
				{
					...data,
					[propertyKey]: [],
				},
				false,
				true,
				"hideRecipient"
			);
		}
	};

	const buildMultiselectComponent = (
		type: RecipientType,
		actions?: ActionItemProps[]
	) => {
		const items: MultiSelectItemProps[] = (
			data[type.toLowerCase() as keyof EmailData] as RecipientData[]
		).map((x: RecipientData) => {
			return {
				key: x.address ?? x.name,
				value:
					`${x.name || x.address} ${!!x.name && !!x.address ? `<${x.address}>` : ""}`.trim(),
				action: () => removeRecipient(type, x.address ?? x.name),
				appearance: !x.isValid ? "error" : undefined,
				contactId: undefined,
				selectionSource: undefined,
				selectionType: undefined,
				disabled: isReadOnlyMode,
			};
		});

		return (
			<Multiselect
				value={searchValues[type]}
				disabled={isReadOnlyMode}
				actions={actions}
				label={intl.formatMessage({ id: `Interaction.EmailEditor.${type}` })}
				classesName={{
					root: classes.multiselectRoot,
					label: classes.recipientsLabel,
					input: classes.multiInputElement,
				}}
				items={items}
				onItemSelected={(item: ItemData) => {
					addReceiver(type, {
						name: item.name,
						address: item.value.trim().toLocaleLowerCase(),
					});
					handleSearchChange(type, "");
				}}
				onInputChanged={(value: string) => {
					handleSearchChange(type, value);
				}}
				queryFn={(e: string) =>
					searchContactsByEmail(e).then((x: ShortContactInfo[]) =>
						x.map((s) => ({
							key: s.id,
							header: s.name,
							content: [s.jobTitle, s.department].filter((x) => !!x).join("/"),
							data: { name: s.name, value: s.email },
						}))
					)
				}
				isKeyDownHandled={true}
			/>
		);
	};

	const recipientsView = () => {
		const dynamicReceivers = [RecipientType.Cc, RecipientType.Bcc];

		const actions: ActionItemProps[] = !isReadOnlyMode
			? dynamicReceivers
					.filter((x: RecipientType) => !visibleActions.has(x))
					.map((x: RecipientType) => ({
						label: intl.formatMessage({ id: `Interaction.EmailEditor.${x}` }),
						action: () => {
							setHiddenRecipients((prev) => {
								const next = new Set(prev);
								next.delete(x);

								return next;
							});
							setVisibleActions((prev) =>
								!prev.has(x) ? new Set([...prev, x]) : prev
							);
						},
					}))
			: [];

		const dismissibleRecipient = (type: RecipientType) => (
			<>
				<Divider />
				<Flex vAlign="center">
					<div className={classes.dismissibleRecipientField}>
						{buildMultiselectComponent(type)}
					</div>
					{!isReadOnlyMode && (
						<Button
							appearance="subtle"
							icon={<Dismiss16Regular />}
							className={classes.recipientDismissButton}
							title={intl.formatMessage({
								id: "Interaction.EmailEditor.Remove",
							})}
							onClick={() => hideRecipient(type)}
						/>
					)}
				</Flex>
			</>
		);

		return (
			<>
				{buildMultiselectComponent(RecipientType.To, actions)}
				{visibleActions.has(RecipientType.Cc) &&
					dismissibleRecipient(RecipientType.Cc)}
				{visibleActions.has(RecipientType.Bcc) &&
					dismissibleRecipient(RecipientType.Bcc)}
			</>
		);
	};

	const { validateAndUploadDraftAttachments, deleteDraftAttachment } =
		useEmailAttachments(data, onChange, sessionId ?? "");

	const onFilesSelected = useCallback(
		(files: FileList) => {
			const fileArray = Array.from(files);
			validateAndUploadDraftAttachments(fileArray);
		},
		[data, onChange, validateAndUploadDraftAttachments]
	);

	const onClick = () => {
		onSendClick(currentInteraction?.sendType ?? SendType.Send);
	};

	const disabledTemplateTypes = useMemo(() => {
		const disabled: TemplateType[] = [];
		if (props.isOutboundConversationStart) disabled.push(TemplateType.AIAssistantResponse);
		if (isAicLocked) {
			disabled.push(TemplateType.AIPrompt, TemplateType.AIAssistantResponse);
		}
		return disabled;
	}, [props.isOutboundConversationStart, isAicLocked]);

	const isAttachmentUploadInProgress = useMemo(() => {
		return data.attachments.some(
			(x) => x.uploadStatus == UploadStatus.InProgress
		);
	}, [data]);

	const { isDragActive, dragProps } = useDragAndDrop({
		onDrop: validateAndUploadDraftAttachments,
		disabled: isReadOnlyMode,
	});

	const currentAttachmentsSizeBytes = useMemo(
		() => calculateAttachmentsSize(data.attachments),
		[data.attachments]
	);

	const [isThreadHistoryVisible, setIsThreadHistoryVisible] =
		useState<boolean>(false);

	const isForwardDraft =
		data.refMessage?.actionType === EmailAction.Forward ||
		(emailAction === EmailAction.Forward && !!draftParentMessageId);

	const parentMessageId = draftParentMessageId ?? data.refMessage?.id;

	const {
		data: messageBodyWithHistory,
		isLoading: isBodyWithThreadHistoryLoading,
	} = useQuery({
		queryKey: [MessageBodyKey, parentMessageId],
		throwOnError: true,
		queryFn: () =>
			fetchMessageBody(sessionId ?? "", false, parentMessageId ?? ""),
		enabled:
			isForwardDraft &&
			isThreadHistoryVisible &&
			!!parentMessageId &&
			!!sessionId,
	});

	const inlineAttachments = useInlineAttachments({
		sessionId: sessionId ?? "",
		messageId: parentMessageId ?? "",
		isHistory: false,
		isPreview: false,
	});

	useEffect(() => {
		if (
			isForwardDraft &&
			isThreadHistoryVisible &&
			!!sessionId &&
			!!parentMessageId
		) {
			inlineAttachments.trigger();
		}
	}, [isForwardDraft, isThreadHistoryVisible, sessionId, parentMessageId]);

	const resolvedThreadHistoryHtml = useMemo(() => {
		const content = messageBodyWithHistory?.content ?? "";
		if (!content || !parentMessageId) {
			return content;
		}

		return sanitizeEmailBody(
			content,
			parentMessageId,
			inlineAttachments.inline
		);
	}, [messageBodyWithHistory, inlineAttachments.inline, parentMessageId]);

	const handleAttachmentRemove = useCallback(
		(attachment: any) => {
			deleteDraftAttachment(attachment);
			onAttachmentDeleted?.();
		},
		[deleteDraftAttachment, onAttachmentDeleted]
	);

	return (
		<Flex
			column
			className={mergeClasses(
				classes.root,
				!isDarkTheme ? classes.rootHighlighted : classes.rootHighlightedDark
			)}
		>
			{recipientsView()}
			{Boolean(
				props.isFirstEmailInConversation || props.isSubjectEditingEnabled
			) && (
				<EmailSubjectInput
					onChange={onChange}
					data={data}
					isReadOnlyMode={isReadOnlyMode}
					emailHasDraft={true}
					isFirstEmailInConversation={props.isFirstEmailInConversation}
				/>
			)}
			<div
				style={{
					position: "relative",
				}}
				{...dragProps}
			>
				<AttachmentsView
					onAttachmentRemove={handleAttachmentRemove}
					data={data}
					isDragActive={!isReadOnlyMode && isDragActive}
					isRemoveAllowed={!isReadOnlyMode}
				/>
				<Divider />
				<LexicalEditorWrapper
					readOnly={isReadOnlyMode}
					initialHtml={data.body}
					onPaste={handleEmailPaste}
					onPastedFiles={
						isReadOnlyMode ? undefined : validateAndUploadDraftAttachments
					}
					onChange={(html: string) => {
						setCurrentBody(html);
						if (hasAiContentRef.current && isBodyEmpty(html)) {
							hasAiContentRef.current = false;
							setHasAiContent(false);
						}
						onChange(
							{ ...data, body: html, hasAiContent: hasAiContentRef.current },
							true,
							true,
							"ConversationEmailEditor.LexicalEditorWrapper.onChange"
						);
					}}
					currentAttachmentsSizeBytes={currentAttachmentsSizeBytes}
					maxTotalAttachmentsSizeBytes={emailMaxTotalAttachmentSizeBytes}
					maxAttachmentSizeBytes={emailMaxAttachmentSizeBytes}
					afterContent={
						<>
							{hasAiContent && !isBodyEmpty(currentBody) && (
								<Flex vAlign="center" className={classes.aiIndicatorContainer}>
									<AiIndicator mode="icon-with-text" />
								</Flex>
							)}
							{isForwardDraft ? (
								<div className={classes.threadHistoryContainer}>
									<Button
										size="small"
										appearance="subtle"
										icon={<MoreHorizontalRegular />}
										title={
											isThreadHistoryVisible
												? intl.formatMessage({
														id: "Interaction.EmailEditor.HideMessageHistory",
													})
												: intl.formatMessage({
														id: "Interaction.EmailEditor.ShowMessageHistory",
													})
										}
										onClick={() =>
											setIsThreadHistoryVisible(!isThreadHistoryVisible)
										}
										className={classes.threadHistoryToggleButton}
									/>
									{isThreadHistoryVisible && (
										<div className={classes.threadHistoryContainer}>
											{isBodyWithThreadHistoryLoading ? (
												<Spinner size="small" />
											) : (
												<div
													dangerouslySetInnerHTML={{
														__html: resolvedThreadHistoryHtml ?? "",
													}}
												/>
											)}
										</div>
									)}
								</div>
							) : undefined}
						</>
					}
					additionalBar={(_editor?: LexicalEditor) =>
						currentInteractionIsPreview ? (
							<></>
						) : (
							<Flex
								className={classes.bottomContainer}
								gap={GapSize.ExtraSmall}
								vAlign="center"
							>
								<Menu positioning="below-start">
									<MenuTrigger disableButtonEnhancement>
										{(triggerProps: MenuButtonProps) => (
											<SplitButton
												menuButton={triggerProps}
												disabled={
													!data.to.length ||
													isTextMonitoring ||
													isAttachmentUploadInProgress ||
													isSendDisabled
												}
												title={
													isAttachmentUploadInProgress
														? intl.formatMessage({
																id: "Interaction.EmailEditor.FileUploadInProgress",
															})
														: ""
												}
												appearance="primary"
												className={classes.sendButton}
												primaryActionButton={{ onClick }}
											>
												{intl.formatMessage({
													id: SendTypeToLabelMapping[
														currentInteraction?.sendType ?? SendType.Send
													],
												})}
											</SplitButton>
										)}
									</MenuTrigger>
									<MenuPopover>
										<MenuList>
											{sendTypes
												.filter((type) => type !== currentInteraction?.sendType)
												.map((type: number) => (
													<MenuItem
														key={type}
														onClick={() => {
															onSendClick(type ?? SendType.Send);
														}}
													>
														{intl.formatMessage({
															id: SendTypeToLabelMapping[type as SendType],
														})}
													</MenuItem>
												))}
										</MenuList>
									</MenuPopover>
								</Menu>
								{!isReadOnlyMode && (
									<FileInput
										accept="*/*"
										onFilesSelected={onFilesSelected}
										disabled={
											currentAttachmentsSizeBytes >= emailMaxTotalAttachmentSizeBytes
										}
									/>
								)}
								{!isTextMonitoring && (
									<TemplateDropdown
										isEditable={true}
										editor={_editor}
										disabledTemplateTypes={disabledTemplateTypes}
										aicLockedTemplateTypes={isAicLocked ? [TemplateType.AIPrompt, TemplateType.AIAssistantResponse] : []}
										onTemplateApplied={(isAi, insertType) => {
											if (isAi) {
												hasAiContentRef.current = true;
												setHasAiContent(true);
											} else if (insertType === InsertType.Replacement) {
												hasAiContentRef.current = false;
												setHasAiContent(false);
											}
										}}
									/>
								)}
								<Button
									className={mergeClasses(
										classes.pushToRight,
										classes.discardButton
									)}
									disabled={props.isOutboundConversationStart || isReadOnlyMode}
									appearance="secondary"
									icon={<BinRecycle20Regular />}
									onClick={() => {
										closeEditor();
									}}
								>
									{intl.formatMessage({
										id: "Interaction.EmailEditor.Delete",
									})}
								</Button>
							</Flex>
						)
					}
				/>
			</div>
		</Flex>
	);
};
